import { Button, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Auth } from 'aws-amplify';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alerter, AlerterVariant } from 'views/alerter';

const useStyles = makeStyles(theme => ({
  input: {
    width: '100%',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

interface VerifyEmailProps {
  username: string;
  completed: () => void;
}

interface ICodeDeliveryDetails {
  AttributeName: string;
  DeliveryMedium: string;
  Destination: string;
}

const VerifyEmail: React.FunctionComponent<VerifyEmailProps> = props => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = React.useState('');
  const [deliveryDetails, setDeliveryDetails] = React.useState<
    ICodeDeliveryDetails | undefined
  >(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const sendCode = React.useCallback(async () => {
    setError(undefined);
    try {
      const result = await Auth.resendSignUp(props.username);
      setDeliveryDetails(result.CodeDeliveryDetails);
    } catch (ex: any) {
      setError(
        ex.code === 'LimitExceededException'
          ? t('forgot_password.too_many_code_requests')
          : ex.message
      );
    }
  }, [props.username, t]);

  React.useEffect(() => {
    sendCode();
  }, [sendCode]);

  const codeIsValid = /^\d{6}$/.test(verificationCode);

  return (
    <React.Fragment>
      <Typography variant="body1">
        {t('forgot_password.verify_email_required')}
        {deliveryDetails?.DeliveryMedium.toLowerCase() === 'email' &&
          t('forgot_password.verify_email_code_sent', {
            email: deliveryDetails.Destination,
          })}
      </Typography>
      {deliveryDetails && (
        <TextField
          className={styles.input}
          style={{ marginBottom: '1em' }}
          label={t('forgot_password.verification_code')}
          variant="outlined"
          onChange={e => setVerificationCode(e.currentTarget.value)}
        />
      )}
      <Button
        color="primary"
        variant="contained"
        disabled={!codeIsValid}
        onClick={async () => {
          setError(undefined);
          try {
            await Auth.confirmSignUp(props.username, verificationCode);
            props.completed();
          } catch (ex: any) {
            setError(ex.message);
          }
        }}
      >
        {t('forgot_password.verify_email_continue')}
      </Button>
      <Button variant="contained" style={{ float: 'right' }} onClick={sendCode}>
        {t('forgot_password.code_retry')}
      </Button>
      {error && (
        <Alerter style={{ marginTop: '1em' }} variant={AlerterVariant.error}>
          {error}
        </Alerter>
      )}
    </React.Fragment>
  );
};

export default VerifyEmail;
